import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useI18n } from '@whizz/react-i18n';
import type { UsePaginationInstanceProps } from 'react-table';
import ButtonIcon from '../../button/Button.icon';
import Input from '../../input/Input';
import { GLOBAL_ICONS } from '../../../util/vars';
import { debounce } from 'lodash';

type Props = {
  pageIndex: number;
  pageCount: number;
  showFastJumpButton: boolean;
  scrollContainer?: Window | Element | null;
} & Pick<UsePaginationInstanceProps<Record<number, null>>, 'canPreviousPage' | 'previousPage' | 'canNextPage' | 'nextPage' | 'gotoPage'>;

const Pagination = ({
  pageIndex,
  pageCount,
  showFastJumpButton,
  canPreviousPage,
  previousPage,
  canNextPage,
  nextPage,
  gotoPage,
  scrollContainer = window,
}: Props): JSX.Element => {

  const { translate } = useI18n();
  const [ page, setPage ] = useState<string>(() => String(pageIndex + 1));
  const previousPageIndex = useRef(pageIndex);
  const debouncedGotoPage = useCallback(debounce(gotoPage, 750), [ gotoPage ]);

  useEffect(() => {
    if (page !== '') {
      const pageIndex = Number(page) - 1;
      debouncedGotoPage(pageIndex);
    } else {
      debouncedGotoPage.cancel();
    }
    return () => debouncedGotoPage.cancel();
  }, [ page ]);

  useEffect(() => {
    if (previousPageIndex.current !== pageIndex) {
      setPage(String(pageIndex + 1));
      previousPageIndex.current = pageIndex;
      scrollContainer?.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [ pageIndex, scrollContainer ]);

  return (
    <div className={'flex justify-center items-center gap-2'}>
      {showFastJumpButton && (
        <ButtonIcon
          icon={GLOBAL_ICONS.first}
          size={'sm'}
          title={translate('components.TABLES.PAGINATION.FIRST_PAGE')}
          disabled={!canPreviousPage}
          onClick={() => gotoPage(0)}
        />
      )}
      <ButtonIcon
        icon={GLOBAL_ICONS.previous}
        size={'sm'}
        title={translate('components.TABLES.PAGINATION.PREVIOUS_PAGE')}
        disabled={!canPreviousPage}
        onClick={previousPage}
      />
      <div>{translate('components.TABLES.PAGINATION.PAGE_COUNT', { page: pageIndex + 1, total: pageCount })}</div>
      <ButtonIcon
        icon={GLOBAL_ICONS.next}
        size={'sm'}
        title={translate('components.TABLES.PAGINATION.NEXT_PAGE')}
        disabled={!canNextPage}
        onClick={nextPage}
      />
      {showFastJumpButton && (
        <ButtonIcon
          icon={GLOBAL_ICONS.last}
          size={'sm'}
          title={translate('components.TABLES.PAGINATION.LAST_PAGE')}
          disabled={!canNextPage}
          onClick={() => gotoPage(pageCount - 1)}
        />
      )}
      <div> | {translate('components.TABLES.PAGINATION.GO_TO_PAGE')}</div>
      <Input
        id={'pagination_input'}
        label={translate('components.TABLES.PAGINATION.GO_TO_PAGE')}
        hideLabel
        type={'number'}
        value={page}
        min={1}
        max={pageCount}
        onChange={(e) => {
          const page = e.target.value ? e.target.value  : '';
          setPage(page);
        }}
        style={{
          width: 80,
        }}
      />
    </div>
  );
};

export default Pagination;
